import React, { useState } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarIcon, Clock, Building2, StickyNote, Copy, Link, Unlink, AlertTriangle } from 'lucide-react';
import type { Room, Booking, BookingStatus } from './BookingCalendar';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { formatCurrency } from '@/lib/utils';
import { ToggleWithLabel } from '@/components/ui/toggle-with-label';
import { TimeSelect } from '@/components/ui/time-select';
import { useBookings } from '@/contexts/BookingContext';
import { LinkedBookingIndicator } from './LinkedBookingIndicator';
import { DuplicateBookingDialog } from './DuplicateBookingDialog';
import { ConflictWarningDialog } from './ConflictWarningDialog';
import { validateBookingTime } from '@/lib/booking-utils';
import { useToast } from '@/hooks/use-toast';
import { staffConfig } from '@/config/staff';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

interface EditBookingDialogProps {
  booking: Booking;
  room: Room;
  onClose: () => void;
  onUpdate: (updatedBooking: Booking) => void;
  onDelete: (bookingId: number) => void;
  onDuplicate?: (date: Date) => void;
}

const statusStyles = {
  paid: 'px-3 py-1.5 w-[200px] bg-blue-100 border-l-4 border-blue-500 text-blue-800 hover:bg-blue-200/80 transition-all duration-200',
  booked: 'px-3 py-1.5 w-[200px] bg-emerald-100 border-l-4 border-emerald-500 text-emerald-800 hover:bg-emerald-200/80 transition-all duration-200',
  pending: 'px-3 py-1.5 w-[200px] bg-amber-100 border-l-4 border-amber-500 text-amber-800 hover:bg-amber-200/80 transition-all duration-200'
};

export function EditBookingDialog({
  booking,
  room,
  onClose,
  onUpdate,
  onDelete,
  onDuplicate
}: EditBookingDialogProps) {
  const { breakLink, getLinkedBookingsForId, checkDuplicate } = useBookings();
  const { toast } = useToast();
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [conflictBooking, setConflictBooking] = useState<Booking | null>(null);
  const linkedBookings = getLinkedBookingsForId(booking.id);
  const hasLinks = linkedBookings.length > 0;
  const [calendarOpen, setCalendarOpen] = useState(false);
  const isMobile = window.innerWidth <= 768;

  const [form, setForm] = useState({
    bookedBy: booking.bookedBy,
    clientName: booking.clientName,
    timeSlot: booking.timeSlot,
    duration: booking.duration.toString(),
    status: booking.status,
    isFullDay: booking.duration === 14 && booking.timeSlot === '09:00',
    amount: booking.revenue?.amount || 0,
    hasNotes: booking.revenue?.hasNotes || false,
    notes: booking.revenue?.notes || '',
    date: new Date(booking.date)
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const updatedBooking: Booking = {
      ...booking,
      bookedBy: form.bookedBy,
      clientName: form.clientName,
      timeSlot: form.isFullDay ? '09:00' : form.timeSlot,
      duration: form.isFullDay ? 14 : parseInt(form.duration),
      status: form.status as BookingStatus,
      revenue: {
        amount: isSuHeSelected ? 0 : form.amount,
        hasNotes: form.hasNotes,
        notes: form.hasNotes ? form.notes : undefined
      },
      date: form.date.toISOString()
    };

    // Validate booking time
    const timeError = validateBookingTime(updatedBooking);
    if (timeError) {
      toast({
        title: "Invalid Time",
        description: timeError,
        variant: "destructive"
      });
      return;
    }

    // Check for conflicts
    const conflict = checkDuplicate(updatedBooking, booking.id);
    if (conflict) {
      setConflictBooking(updatedBooking);
      setShowConflictWarning(true);
      return;
    }

    onUpdate(updatedBooking);
    onClose();
  };

  const handleDelete = () => {
    onDelete(booking.id);
    onClose();
  };

  const handleDurationChange = (value: string) => {
    if (value === 'full') {
      setForm(prev => ({ 
        ...prev, 
        duration: '14', 
        isFullDay: true,
        timeSlot: '09:00' // Always set to 9:00 for full day bookings
      }));
    } else {
      setForm(prev => ({ 
        ...prev, 
        duration: value, 
        isFullDay: false 
      }));
    }
  };

  const handleBreakLink = () => {
    if (window.confirm('Are you sure you want to break the link with all duplicates? This cannot be undone.')) {
      breakLink(booking.id);
    }
  };

  const handleTimeChange = (value: string) => {
    if (form.isFullDay) {
      // If full day is selected, keep the time at 09:00
      return;
    }
    setForm(prev => ({ ...prev, timeSlot: value }));
  };

  const handleAlternativeSelect = (newDate: string, newTime: string) => {
    if (conflictBooking) {
      const updatedBooking = {
        ...conflictBooking,
        date: newDate,
        timeSlot: newTime
      };
      onUpdate(updatedBooking);
      onClose();
    }
  };

  const handleDuplicateComplete = (date: Date) => {
    onDuplicate?.(date);
    onClose();
  };

  const isSuHeSelected = form.bookedBy === 'SuHe';

  return (
    <>
      <Dialog open onOpenChange={onClose} modal>
        <DialogContent className={cn(
          "sm:max-w-[425px]",
          isMobile && "w-[95vw] p-4 max-h-[95vh]"
        )}>
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Edit Booking</span>
              {hasLinks && <LinkedBookingIndicator booking={booking} />}
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className={cn(
            "pr-4 -mr-4",
            isMobile ? "max-h-[calc(95vh-8rem)]" : "max-h-[calc(85vh-8rem)]"
          )}>
          <div className={cn(
            "bg-gray-50 rounded-lg space-y-3 mb-4",
            isMobile ? "p-3" : "p-4"
          )}>
            <div className="flex items-center gap-2 text-sm">
              <CalendarIcon className="h-4 w-4" />
              <span>{format(form.date, 'PPP')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4" />
              <div className="flex items-center gap-2">
                <span>Start Time:</span>
                <TimeSelect
                  value={form.timeSlot}
                  onChange={handleTimeChange}
                  showIcon={false}
                  className="w-[100px] h-8"
                  disabled={form.isFullDay}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="h-4 w-4" />
              <span>{room.name}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Date & Time</Label>
              <div className={cn(
                "flex gap-2",
                isMobile && "flex-col"
              )}>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "justify-start text-left font-normal",
                        isMobile ? "w-full" : "w-[280px]",
                        !form.date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.date ? format(form.date, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(date) => {
                        setForm(prev => ({ ...prev, date: date || new Date() }));
                        setCalendarOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <TimeSelect
                  value={form.timeSlot}
                  onChange={handleTimeChange}
                  className={isMobile ? "w-full" : undefined}
                  disabled={form.isFullDay}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bookedBy">Booked By</Label>
              <Select 
                value={form.bookedBy}
                onValueChange={(value) => {
                  setForm(prev => ({
                    ...prev,
                    bookedBy: value,
                    clientName: value === 'SuHe' ? 'SuHe' : prev.clientName,
                    amount: value === 'SuHe' ? 0 : prev.amount
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staffConfig.map(staff => (
                    <SelectItem key={staff.id} value={staff.name}>
                      <span className={cn("px-2 py-1 rounded-md", staff.color)}>
                        {staff.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clientName">Client Company</Label>
              <Input
                id="clientName"
                value={isSuHeSelected ? 'SuHe' : form.clientName}
                onChange={(e) => setForm({...form, clientName: e.target.value})}
                disabled={isSuHeSelected}
                className={cn(
                  "hover:border-gray-400 focus:border-purple-500 transition-colors",
                  isSuHeSelected && "bg-gray-50"
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">Duration</Label>
              <Select 
                value={form.isFullDay ? 'full' : form.duration}
                onValueChange={handleDurationChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full" className="font-medium text-purple-600">
                    Full Day (9:00-23:00)
                  </SelectItem>
                  <SelectItem value="1">1 hour</SelectItem>
                  {Array.from({ length: 7 }, (_, i) => (
                    <SelectItem key={i + 2} value={(i + 2).toString()}>
                      {i + 2} hours
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select 
                value={form.status}
                onValueChange={(value) => setForm({...form, status: value as BookingStatus})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">
                    <div className={cn("px-3 py-1 rounded-md", statusStyles.paid)}>
                      Paid
                    </div>
                  </SelectItem>
                  <SelectItem value="booked">
                    <div className={cn("px-3 py-1 rounded-md", statusStyles.booked)}>
                      Booked
                    </div>
                  </SelectItem>
                  <SelectItem value="pending">
                    <div className={cn("px-3 py-1 rounded-md", statusStyles.pending)}>
                      Pending
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Amount (€)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  €
                </span>
                <Input
                  id="amount"
                  type="text"
                  inputMode="decimal"
                  pattern="^\d*\.?\d{0,2}$"
                  className="pl-7"
                  value={isSuHeSelected ? '0' : form.amount.toString()}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    const parts = value.split('.');
                    if (parts.length > 2) return;
                    if (parts[1]?.length > 2) return;
                    
                    setForm(prev => ({
                      ...prev,
                      amount: value === '' ? 0 : parseFloat(value) || 0
                    }));
                  }}
                  disabled={isSuHeSelected}
                  style={{ appearance: 'textfield' }}
                />
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <ToggleWithLabel
                id="notes-enabled"
                checked={form.hasNotes}
                onChange={(checked) => 
                  setForm(prev => ({
                    ...prev,
                    hasNotes: checked
                  }))
                }
                label="Add Notes"
                leftIcon={<StickyNote className="h-4 w-4" />}
              />

              {form.hasNotes && (
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add any notes..."
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      notes: e.target.value
                    }))}
                  />
                </div>
              )}
            </div>

            <div className={cn(
              "grid gap-3 pt-4",
              isMobile ? "grid-cols-1" : "grid-cols-2"
            )}>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "flex items-center justify-center gap-2",
                  isMobile ? "w-full py-3" : "w-full"
                )}
                onClick={() => setShowDuplicateDialog(true)}
              >
                <Copy className="h-4 w-4" />
                Duplicate
              </Button>
              {hasLinks ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2 text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700"
                  onClick={handleBreakLink}
                >
                  <Unlink className="h-4 w-4" />
                  Break Link
                </Button>
              ) : null}
            </div>

            <div className={cn(
              "grid gap-3",
              isMobile ? "grid-cols-1" : "grid-cols-2"
            )}>
              <Button 
                type="button" 
                variant="destructive" 
                onClick={() => setShowDeleteConfirm(true)}
                className={cn(
                  "flex items-center justify-center gap-2",
                  isMobile ? "w-full py-3" : "w-full"
                )}
              >
                <AlertTriangle className="h-4 w-4" />
                Delete
              </Button>
              <Button 
                type="submit"
                className={cn(
                  "bg-purple-600 hover:bg-purple-700",
                  isMobile ? "w-full py-3" : "w-full"
                )}
              >
                Update
              </Button>
            </div>
          </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {showDuplicateDialog && (
        <DuplicateBookingDialog
          booking={booking}
          onClose={() => setShowDuplicateDialog(false)}
          onDuplicate={handleDuplicateComplete}
        />
      )}

      {showConflictWarning && conflictBooking && (
        <ConflictWarningDialog
          conflict={checkDuplicate(conflictBooking, booking.id)!}
          rooms={[room]}
          open={showConflictWarning}
          onClose={() => setShowConflictWarning(false)}
          onSelectAlternative={handleAlternativeSelect}
        />
      )}

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className={cn(
          isMobile && "w-[95vw] p-4"
        )}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Booking
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this booking.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete Booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}